import type { ReactElement } from 'react';
import { Kanban, ExternalLink, User, DollarSign, ChevronRight } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

interface DealSnippetProps {
  deal: Extract<StructuredMessagePayload, { type: 'deal' }>['deal'];
  onOpenEntity?:
    | ((type: 'deal' | 'contact', id: string, title?: string | undefined) => void)
    | undefined;
  compact?: boolean | undefined;
  className?: string | undefined;
}

const STAGE_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  New: {
    bg: 'bg-zinc-100 text-zinc-700 border-zinc-200/80',
    text: 'text-zinc-700',
    dot: 'bg-zinc-400',
  },
  Contacted: {
    bg: 'bg-amber-50/80 text-amber-800 border-amber-200/60',
    text: 'text-amber-800',
    dot: 'bg-amber-500',
  },
  Negotiation: {
    bg: 'bg-blue-50/80 text-blue-800 border-blue-200/60',
    text: 'text-blue-800',
    dot: 'bg-blue-500',
  },
  Won: {
    bg: 'bg-emerald-50 text-emerald-800 border-emerald-200/60',
    text: 'text-emerald-800',
    dot: 'bg-emerald-500',
  },
  Lost: {
    bg: 'bg-rose-50 text-rose-800 border-rose-200/60',
    text: 'text-rose-800',
    dot: 'bg-rose-500',
  },
};

export function DealSnippet({
  deal,
  onOpenEntity,
  compact = false,
  className = '',
}: DealSnippetProps): ReactElement {
  const { t, locale } = useTranslation();
  const amount =
    deal.amount === undefined || deal.currency === undefined
      ? null
      : `${new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US').format(deal.amount)} ${deal.currency}`;

  const defaultStage = STAGE_CONFIG.New ?? {
    bg: 'bg-zinc-100 text-zinc-700 border-zinc-200/80',
    text: 'text-zinc-700',
    dot: 'bg-zinc-400',
  };
  const stageConfig = (deal.stage ? STAGE_CONFIG[deal.stage] : undefined) ?? defaultStage;

  const handleClick = () => {
    onOpenEntity?.('deal', deal.id, deal.title);
  };

  // ─── LobeHub High-Density Row View ───────────────────────────────────────────
  if (compact) {
    return (
      <div
        data-testid="structured-snippet-deal"
        onClick={handleClick}
        className={`group flex items-center justify-between gap-3 px-3.5 py-2 text-xs transition-colors hover:bg-zinc-50/80 cursor-pointer ${className}`}
      >
        <div className="flex min-w-0 items-center gap-2.5 flex-1">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
            <Kanban size={13} />
          </div>
          <span className="font-medium text-zinc-900 group-hover:text-zinc-950 truncate">
            {deal.title}
          </span>
          {deal.contactName && (
            <span className="text-[11px] text-zinc-400 shrink-0">• {deal.contactName}</span>
          )}
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {amount && (
            <span className="font-mono font-medium text-zinc-800 text-[11px]">{amount}</span>
          )}

          {deal.stage && (
            <span
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${stageConfig.bg}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${stageConfig.dot}`} />
              {deal.stage}
            </span>
          )}

          <ChevronRight
            size={13}
            className="text-zinc-400 group-hover:text-zinc-700 transition-transform group-hover:translate-x-0.5"
          />
        </div>
      </div>
    );
  }

  // ─── Standalone Detailed Card View ───────────────────────────────────────────
  return (
    <div
      data-testid="structured-snippet-deal"
      onClick={handleClick}
      className={`group my-1.5 flex flex-col gap-2 rounded-xl border border-zinc-200/90 bg-white p-3.5 text-xs shadow-2xs transition-all hover:border-zinc-300 cursor-pointer ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
            <Kanban size={14} />
          </div>
          <span className="truncate font-semibold text-zinc-900">{deal.title}</span>
        </div>

        {deal.stage && (
          <span
            className={`flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-medium ${stageConfig.bg}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${stageConfig.dot}`} />
            {deal.stage}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-zinc-100 pt-2 text-[11px] text-zinc-500">
        <div className="flex items-center gap-3">
          {deal.contactName && (
            <span className="flex items-center gap-1 text-zinc-600">
              <User size={11} className="text-zinc-400" />
              <span>{deal.contactName}</span>
            </span>
          )}
          {amount && (
            <span className="flex items-center gap-0.5 font-mono font-medium text-zinc-800">
              <DollarSign size={11} className="text-zinc-500" />
              <span>{amount}</span>
            </span>
          )}
        </div>

        <span className="inline-flex items-center gap-1 font-medium text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity">
          <span>{t('crm.snippets.open')}</span>
          <ExternalLink size={11} />
        </span>
      </div>
    </div>
  );
}
